<template>
  <v-dialog v-model="dialog" persistent scrollable max-width="760px">
    <v-card class="profile-dialog-card entity-form-dialog">
      <div class="profile-dialog-header entity-form-dialog__header">
        <div class="entity-form-dialog__title">
          <v-avatar size="44" color="green lighten-5">
            <v-icon color="primary">mdi-account-school-outline</v-icon>
          </v-avatar>
          <div>
            <span>Students</span>
            <h2>New student</h2>
            <p>Add personal, enrollment, and fee information.</p>
          </div>
        </div>
        <v-btn icon aria-label="Close new student dialog" @click.stop="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-divider />

      <v-card-text class="profile-dialog-body entity-form-dialog__body">
        <section class="entity-form-dialog__section">
          <div class="entity-form-dialog__section-heading">
            <span><v-icon size="19" color="primary">mdi-account-outline</v-icon></span>
            <div>
              <h3>Student information</h3>
              <p>Basic details used throughout Taleem.</p>
            </div>
          </div>

          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="Student.firstName"
                outlined
                dense
                autofocus
                :error-messages="nameErrors"
                :hide-details="nameErrors.length === 0"
                label="First name"
                @change="Student.firstName = properCase(Student.firstName)"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="Student.lastName"
                outlined
                dense
                hide-details
                label="Last name"
                @change="Student.lastName = properCase(Student.lastName)"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-select
                v-model="Student.gender"
                :items="['male', 'female']"
                outlined
                dense
                hide-details
                label="Gender"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="Student.dob"
                outlined
                dense
                hide-details
                type="date"
                label="Date of birth"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="Student.phoneNumber"
                v-mask="'(###) ###-####'"
                outlined
                dense
                hide-details
                label="Phone number"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="Student.email"
                outlined
                dense
                hide-details
                label="Email address"
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="Student.reference"
                outlined
                dense
                hide-details
                label="Reference"
              />
            </v-col>
            <v-col cols="12">
              <v-textarea
                v-model="Student.description"
                outlined
                dense
                hide-details
                auto-grow
                rows="2"
                :label="$t('labels.description')"
              />
            </v-col>
          </v-row>
        </section>

        <section class="entity-form-dialog__section">
          <div class="entity-form-dialog__section-heading">
            <span><v-icon size="19" color="primary">mdi-school-outline</v-icon></span>
            <div>
              <h3>Enrollment and fees</h3>
              <p>Optionally assign a department, teacher, and monthly fee.</p>
            </div>
          </div>

          <v-row>
            <v-col cols="12" sm="6">
              <v-autocomplete
                v-model="selectedClass"
                :items="Classes"
                clearable
                return-object
                item-text="name"
                outlined
                dense
                hide-details
                label="Department"
              >
                <template slot="item" slot-scope="{ item }">
                  <span>{{ item.name }}</span>
                  <v-spacer />
                  <v-chip color="grey lighten-3" small>{{ item.defaultFee | currency }}</v-chip>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col cols="12" sm="6">
              <v-autocomplete
                v-model="selectedTeacher"
                :items="Teacher"
                clearable
                return-object
                item-text="name"
                outlined
                dense
                hide-details
                label="Teacher"
              />
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-text-field
                v-model="fee"
                outlined
                dense
                hide-details
                type="number"
                class="right-input"
                label="Default fee"
                @focus="$event.target.select()"
              />
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-text-field
                v-model="discount"
                :disabled="!selectedClass"
                outlined
                dense
                hide-details
                type="number"
                class="right-input"
                label="Discount"
                @focus="$event.target.select()"
              />
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-text-field
                v-model="charity"
                :disabled="!selectedClass"
                outlined
                dense
                hide-details
                type="number"
                class="right-input"
                label="Charity"
                @focus="$event.target.select()"
              />
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-text-field
                v-model="parentPay"
                readonly
                outlined
                dense
                hide-details
                type="number"
                class="right-input"
                label="Parent pay"
              />
            </v-col>
          </v-row>

          <div class="entity-form-dialog__summary">
            <v-icon size="18" color="primary">mdi-information-outline</v-icon>
            <span>
              Monthly parent responsibility is <strong>{{ balancePayment | currency }}</strong>.
              Sponsor contribution is <strong>{{ sponsor | currency }}</strong>.
            </span>
          </div>
        </section>
      </v-card-text>

      <v-divider />

      <v-card-actions class="profile-dialog-actions entity-form-dialog__actions">
        <v-btn text @click="close()">Cancel</v-btn>
        <v-btn color="primary" depressed :loading="loading" @click="submit()">
          <v-icon left small>mdi-account-plus-outline</v-icon>
          Create student
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from "vuex";
import studentService from "@/modules/Student/service.js";
import classesService from "@/modules/Classes/service.js";
import teacherService from "@/modules/Teacher/service.js";
import GlobalFunction from "@/mixins/GlobalFunction";

const { validationMixin } = require("vuelidate");
const { required } = require("vuelidate/lib/validators");

export default {
  name: "create-student",
  props: {
    createDialog: {
      default: false,
    },
    CustomerId: {
      default: null,
    },
    createFromStore: {
      type: Boolean,
    },
  },
  data() {
    return {
      loading: false,
      Teacher: [],
      Classes: [],
      selectedClass: null,
      selectedTeacher: null,
      dialog: false,
      Student: {
        name: null,
        email: null,
        gender: "male",
        phoneNumber: null,
        reference: null,
        note: null,
        fee: 0,
        discount: 0,
        sponsor: 0,
        parentPay: 0,
        charity: 0,
      },
      fee: 0,
      discount: 0,
      sponsor: 0,
      parentPay: 0,
      charity: 0,
    };
  },
  mixins: [validationMixin, GlobalFunction],
  validations: {
    Student: {
      firstName: {
        required,
      },
    },
  },
  watch: {
    selectedClass(val) {
      if (val) {
        this.fee = val.defaultFee;
        this.parentPay = val.defaultFee;
      }
    },
    discount(val) {
      this.parentPay = this.fee - val - this.charity;
    },
    parentPay(val) {
      this.sponsor = this.fee - this.discount - val;
    },
    fee(val) {
      this.parentPay = val - this.discount - this.charity;
    },
    charity(val) {
      this.parentPay = this.fee - this.discount - val
    },
    createDialog(val) {
      this.dialog = val;
      this.getClasses();
      this.getTeacher();
    },
  },
  computed: {
    ...mapGetters("global", ["currentUser"]),
    balancePayment() {
      return this.fee - this.discount - this.sponsor;
    },
    nameErrors() {
      const errors = [];
      if (!this.$v.Student.firstName.$dirty) return errors;
      if (!this.$v.Student.firstName.required)
        errors.push("First name is required");

      return errors;
    },
  },
  methods: {
    getClasses() {
      return classesService.getAll().then((response) => {
        console.log("response", response);
        this.Classes = response.data;
        return response;
      });
    },
    getTeacher() {
      return teacherService.getAll().then((response) => {
        this.Teacher = response.data;

        // this.StudentList = this.Teacher.Students;
        return response;
      });
    },
    close() {
      this.empty();
      this.$emit("close");
    },
    empty() {
      this.Student = {
        name: null,
        email: null,
        gender: "male",
        phoneNumber: null,
        reference: null,
        note: null,
        fee: 0,
        discount: 0,
        charity: 0,
        sponsor: 0,
      };
      this.fee = 0;
      this.discount = 0;
      this.charity = 0;
      this.sponsor = 0;
      this.parentPay = 0;
      this.selectedClass = null;
      this.$v.$reset();
    },
    submit() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: this.$t("toastMessage.text.invalidFields"),
          });
      } else {
        this.loading = true;
        this.Student.CustomerId = this.CustomerId;
        console.log("student", this.Student);
        if (this.selectedClass) {
          this.Student.ClassId = this.selectedClass.id;
        }
        if (this.selectedTeacher) {
          this.Student.TeacherId = this.selectedTeacher.id;
        }
        this.Student.fee = this.fee;
        this.Student.discount = this.discount;
        this.Student.charity = this.charity;
        this.Student.sponsor = this.sponsor;
        this.Student.parentPay = this.parentPay;
        return studentService
          .create(this.Student)
          .then((result) => {
            if (result.status) {
              this.$swal
                .mixin({
                  toast: true,
                  position: "top-end",
                  showConfirmButton: false,
                  timer: 3000,
                })
                .fire({
                  icon: "success",
                  title: "Student is created",
                });
              this.loading = false;
              this.$emit("submit");
              this.close();
            }
          })
          .catch((err) => {
            this.loading = false;
            this.$swal
              .mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 3000,
              })
              .fire({
                icon: "error",
                title: err.data.message,
              });
          });
      }
    },
  },
};
</script>
